import { createSlice } from "@reduxjs/toolkit";
import { PURGE } from "redux-persist";
import {
  brokerageAnniversariesLoading,
  brokerageAnniversariesLoaded,
  brokerageAnniversariesError,
  createBrokerageAnniversaryPending,
  createBrokerageAnniversaryFulfilled,
  createBrokerageAnniversaryRejected,
  updateBrokerageAnniversaryPending,
  updateBrokerageAnniversaryFulfilled,
  updateBrokerageAnniversaryRejected,
  deleteBrokerageAnniversaryPending,
  deleteBrokerageAnniversaryFulfilled,
  deleteBrokerageAnniversaryRejected,
  activeBrokerageAnniversaryLoading,
  activeBrokerageAnniversaryLoaded,
  activeBrokerageAnniversaryError,
  clearFinancialSettingsErrors,
} from "./financialSettingsStateActions";
import { IBrokerageAnniversaryModel } from "../../../types/types";

interface IBrokerageAnniversariesState {
  loading: boolean;
  error: string | null;
  data: IBrokerageAnniversaryModel[];
  loaded: boolean;
}

interface ICreateBrokerageAnniversaryState {
  pending: boolean;
  error: string | null;
  fulfilled: boolean;
}

interface IUpdateBrokerageAnniversaryState {
  pending: boolean;
  error: string | null;
  fulfilled: boolean;
  updatingId?: number;
}

interface IDeleteBrokerageAnniversaryState {
  pending: boolean;
  error: string | null;
  fulfilled: boolean;
  deletingId?: number;
}

interface IActiveBrokerageAnniversaryState {
  loading: boolean;
  error: string | null;
  data: IBrokerageAnniversaryModel | null;
}

interface FinancialSettingsState {
  brokerageAnniversaries: IBrokerageAnniversariesState;
  createBrokerageAnniversary: ICreateBrokerageAnniversaryState;
  updateBrokerageAnniversary: IUpdateBrokerageAnniversaryState;
  deleteBrokerageAnniversary: IDeleteBrokerageAnniversaryState;
  activeBrokerageAnniversary: IActiveBrokerageAnniversaryState;
}

const initialState: FinancialSettingsState = {
  brokerageAnniversaries: {
    loading: false,
    error: null,
    data: [],
    loaded: false,
  },
  createBrokerageAnniversary: {
    pending: false,
    error: null,
    fulfilled: false,
  },
  updateBrokerageAnniversary: {
    pending: false,
    error: null,
    fulfilled: false,
  },
  deleteBrokerageAnniversary: {
    pending: false,
    error: null,
    fulfilled: false,
  },
  activeBrokerageAnniversary: {
    loading: false,
    error: null,
    data: null,
  },
};

const financialSettingsSlice = createSlice({
  name: "financialSettings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, () => initialState)
      // Brokerage Anniversaries Loading
      .addCase(brokerageAnniversariesLoading, (state) => {
        state.brokerageAnniversaries.loading = true;
        state.brokerageAnniversaries.error = null;
      })
      .addCase(brokerageAnniversariesLoaded, (state, action) => {
        state.brokerageAnniversaries.loading = false;
        state.brokerageAnniversaries.data = action.payload;
        state.brokerageAnniversaries.loaded = true;
        state.brokerageAnniversaries.error = null;
      })
      .addCase(brokerageAnniversariesError, (state, action) => {
        state.brokerageAnniversaries.loading = false;
        state.brokerageAnniversaries.error = action.payload;
      })
      // Create Brokerage Anniversary
      .addCase(createBrokerageAnniversaryPending, (state) => {
        state.createBrokerageAnniversary.pending = true;
        state.createBrokerageAnniversary.error = null;
        state.createBrokerageAnniversary.fulfilled = false;
      })
      .addCase(createBrokerageAnniversaryFulfilled, (state, action) => {
        state.createBrokerageAnniversary.pending = false;
        state.createBrokerageAnniversary.fulfilled = true;
        state.createBrokerageAnniversary.error = null;
        // Add the new item to the list
        state.brokerageAnniversaries.data.push(action.payload);
        // Sort by anniversary date
        state.brokerageAnniversaries.data.sort((a, b) => {
          if (!a.anniversaryDate || !b.anniversaryDate) return 0;
          return a.anniversaryDate.valueOf() - b.anniversaryDate.valueOf();
        });
      })
      .addCase(createBrokerageAnniversaryRejected, (state, action) => {
        state.createBrokerageAnniversary.pending = false;
        state.createBrokerageAnniversary.error = action.payload;
        state.createBrokerageAnniversary.fulfilled = false;
      })
      // Update Brokerage Anniversary
      .addCase(updateBrokerageAnniversaryPending, (state, action) => {
        state.updateBrokerageAnniversary.pending = true;
        state.updateBrokerageAnniversary.error = null;
        state.updateBrokerageAnniversary.fulfilled = false;
        state.updateBrokerageAnniversary.updatingId = action.payload;
      })
      .addCase(updateBrokerageAnniversaryFulfilled, (state, action) => {
        state.updateBrokerageAnniversary.pending = false;
        state.updateBrokerageAnniversary.fulfilled = true;
        state.updateBrokerageAnniversary.error = null;
        state.updateBrokerageAnniversary.updatingId = undefined;
        // Update the item in the list
        const index = state.brokerageAnniversaries.data.findIndex(
          (item) =>
            item.brokerageAnniversaryId ===
            action.payload.brokerageAnniversaryId
        );
        if (index !== -1) {
          state.brokerageAnniversaries.data[index] = action.payload;
          // Re-sort by anniversary date
          state.brokerageAnniversaries.data.sort((a, b) => {
            if (!a.anniversaryDate || !b.anniversaryDate) return 0;
            return a.anniversaryDate.valueOf() - b.anniversaryDate.valueOf();
          });
        }
      })
      .addCase(updateBrokerageAnniversaryRejected, (state, action) => {
        state.updateBrokerageAnniversary.pending = false;
        state.updateBrokerageAnniversary.error = action.payload;
        state.updateBrokerageAnniversary.fulfilled = false;
        state.updateBrokerageAnniversary.updatingId = undefined;
      })
      // Delete Brokerage Anniversary
      .addCase(deleteBrokerageAnniversaryPending, (state, action) => {
        state.deleteBrokerageAnniversary.pending = true;
        state.deleteBrokerageAnniversary.error = null;
        state.deleteBrokerageAnniversary.fulfilled = false;
        state.deleteBrokerageAnniversary.deletingId = action.payload;
      })
      .addCase(deleteBrokerageAnniversaryFulfilled, (state, action) => {
        state.deleteBrokerageAnniversary.pending = false;
        state.deleteBrokerageAnniversary.fulfilled = true;
        state.deleteBrokerageAnniversary.error = null;
        state.deleteBrokerageAnniversary.deletingId = undefined;
        // Remove the item from the list
        state.brokerageAnniversaries.data =
          state.brokerageAnniversaries.data.filter(
            (item) => item.brokerageAnniversaryId !== action.payload
          );
      })
      .addCase(deleteBrokerageAnniversaryRejected, (state, action) => {
        state.deleteBrokerageAnniversary.pending = false;
        state.deleteBrokerageAnniversary.error = action.payload;
        state.deleteBrokerageAnniversary.fulfilled = false;
        state.deleteBrokerageAnniversary.deletingId = undefined;
      })
      // Active Brokerage Anniversary
      .addCase(activeBrokerageAnniversaryLoading, (state) => {
        state.activeBrokerageAnniversary.loading = true;
        state.activeBrokerageAnniversary.error = null;
      })
      .addCase(activeBrokerageAnniversaryLoaded, (state, action) => {
        state.activeBrokerageAnniversary.loading = false;
        state.activeBrokerageAnniversary.data = action.payload;
        state.activeBrokerageAnniversary.error = null;
      })
      .addCase(activeBrokerageAnniversaryError, (state, action) => {
        state.activeBrokerageAnniversary.loading = false;
        state.activeBrokerageAnniversary.error = action.payload;
      })
      // Clear Errors
      .addCase(clearFinancialSettingsErrors, (state) => {
        state.brokerageAnniversaries.error = null;
        state.createBrokerageAnniversary.error = null;
        state.updateBrokerageAnniversary.error = null;
        state.deleteBrokerageAnniversary.error = null;
        state.activeBrokerageAnniversary.error = null;
      });
  },
});

export default financialSettingsSlice.reducer;
